import type { ClientSummaryDto } from '@/services/types';
import { parseDate, parseISOString } from '@/utils/date';

import type { ApiWire } from '../shared/types';

/** One row of the internal clients table. */
export interface ClientSummary {
    id: number;
    name: string;
    archived: boolean;
    createdAt: Date | null;
    featureFlags: string[];
    siteCount: number;
    penCount: number;
    /** Bioscopes placed for the client now, test bioscopes left out. */
    bioscopeCount: number;
    /** Placed bioscopes that have not measured within 28 hours, or never have. */
    staleBioscopeCount: number;
    lastMeasurementAt: Date | null;
    userCount: number;
    contractCount: number;
    /** Every contract row for the client, including archived ones. */
    totalContractCount: number;
    /** Bioscopes licensed across the contracts that are not archived. */
    contractedBioscopeCount: number;
    /**
     * The first license end that has not passed, at local midnight; once all have ended, the
     * last one that did. Null without contract licenses.
     */
    nextLicenseExpiry: Date | null;
    /** License types with a license running today. */
    activeLicenses: string[];
    /** Active license types whose running licenses all end within 30 days. */
    expiringLicenses: string[];
    /** License types with nothing running or about to start; one renewed by a newer license is not. */
    lapsedLicenses: string[];
    apiUserCount: number;
    integrations: string[];
}

export function toClientSummary(dto: ApiWire<ClientSummaryDto>): ClientSummary {
    return {
        id: dto.id,
        name: dto.name,
        archived: dto.archived,
        createdAt: parseISOString(dto.createdAt),
        featureFlags: dto.featureFlags,
        siteCount: dto.siteCount,
        penCount: dto.penCount,
        bioscopeCount: dto.bioscopeCount,
        staleBioscopeCount: dto.staleBioscopeCount,
        lastMeasurementAt: parseISOString(dto.lastMeasurementAt),
        userCount: dto.userCount,
        contractCount: dto.contractCount,
        totalContractCount: dto.totalContractCount,
        contractedBioscopeCount: dto.contractedBioscopeCount,
        // A calendar day: read in the browser's zone so it shows as the same day everywhere.
        nextLicenseExpiry: dto.nextLicenseExpiry ? parseDate(dto.nextLicenseExpiry) : null,
        activeLicenses: dto.activeLicenses,
        expiringLicenses: dto.expiringLicenses,
        lapsedLicenses: dto.lapsedLicenses,
        apiUserCount: dto.apiUserCount,
        integrations: dto.integrations,
    };
}

export function toClientSummaries(dtos: ApiWire<ClientSummaryDto>[]): ClientSummary[] {
    return dtos.map(toClientSummary);
}
